"use client";

import React, { FC } from "react";
import { useRouter } from "next/navigation";
import { useStyles } from "./styles";
import { applyFirstEditionDefaults } from "utils/cta";
import { MdClose } from "react-icons/md";

interface UpgradeTooltipContent {
  modalTitle?: string;
  modalSubtitle?: string;
  progressLabelFree?: string;
  progressLabelPremium?: string;
  disclaimer?: string;
}

interface UpgradeTooltipProps {
  onClose: () => void;
  onUpgrade: () => void;
  versionIdentifier?: string;
  ctaContent?: UpgradeTooltipContent;
  renderAbove?: boolean;
}

const UpgradeTooltip: FC<UpgradeTooltipProps> = ({
  onClose,
  onUpgrade,
  versionIdentifier,
  ctaContent,
  renderAbove = false,
}) => {
  const { classes, cx } = useStyles();
  const router = useRouter();

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onClose();
  };

  const handleUpgradeClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onUpgrade();
    router.push("/subscribe");
  };

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const defaults = {
    modalTitle: "You're almost there!",
    modalSubtitle:
      "Upgrade now to unlock HD generation and create stunning high-resolution images without limits.",
    progressLabelFree: "Free",
    progressLabelPremium: "Premium",
    disclaimer: "No commitments, cancel anytime.",
  };

  const content = applyFirstEditionDefaults(
    versionIdentifier,
    "B",
    defaults,
    ctaContent
  );

  return (
    <div
      className={cx(
        classes.tooltip,
        renderAbove ? classes.tooltipAbove : classes.tooltipBelow
      )}
      onClick={stopPropagation}
    >
      <div
        className={cx(
          classes.tooltipContent,
          renderAbove
            ? classes.tooltipContentAbove
            : classes.tooltipContentBelow
        )}
      >
        <button className={classes.dismiss} onClick={handleCancel}>
          <MdClose />
        </button>

        <div className={classes.progressContainer}>
          <div className={classes.progressLabel}>
            <span>{content.progressLabelFree}</span>
            <span>{content.progressLabelPremium}</span>
          </div>
          <div className={classes.progressBar}>
            <div className={classes.progressFill} />
          </div>
        </div>

        <h3 className={classes.tooltipTitle}>{content.modalTitle}</h3>
        <p className={classes.tooltipSubtitle}>{content.modalSubtitle}</p>

        <button className={classes.ctaButton} onClick={handleUpgradeClick}>
          UPGRADE
        </button>

        <div className={classes.tooltipFooter}>
          <span>{content.disclaimer}</span>
        </div>
      </div>
    </div>
  );
};

export default UpgradeTooltip;
